import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faSearch,
    faCode,
    faShieldAlt
} from "@fortawesome/free-solid-svg-icons";
import { HiOutlineRefresh, HiOutlineFilter, HiX, HiOutlineExclamation } from "react-icons/hi";
import recipeDB_logo from "../assets/recipeDB_logo-removebg-preview.png";
import flavorDB_logo from "../assets/flavorDB_logo-removebg-preview.png";
import sustainableDB_logo from "../assets/sustainableDB_logo.png";
import dietRx_logo from "../assets/dietRx_logo.png";
import spiceRx_logo from "../assets/spicerx.jpg.png";
import cocktailDB_logo from "../assets/cocktailDB.png";

const MotionDiv = motion.div;

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.08
        }
    }
};

const itemVariants = {
    hidden: { y: 10, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
        transition: {
            type: "spring",
            stiffness: 80,
            damping: 12
        }
    }
};

const ApiCatalog = ({ apis, metricsStatus }) => {
    const [selectedApi, setSelectedApi] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortField, setSortField] = useState("usage");
    const [sortOrder, setSortOrder] = useState("desc");
    const [usageFilter, setUsageFilter] = useState("all");
    const [enrichedApis, setEnrichedApis] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [maxApiUsage, setMaxApiUsage] = useState(10000);

    useEffect(() => {
        if (!apis || !Array.isArray(apis) || apis.length === 0) {
            // Create default API entries if the API list is empty or invalid
            const baseApis = [
                { _id: "recipedb", name: "RecipeDB", description: "Recipe database API", subapis: [] },
                { _id: "flavordb", name: "FlavorDB", description: "Flavor database API", subapis: [] },
                { _id: "sustainablefooddb", name: "SustainableFoodDB", description: "Sustainable food database API", subapis: [] },
                { _id: "dietrx", name: "dietRx", description: "Diet prescription API", subapis: [] },
                { _id: "spicerx", name: "SpiceRx", description: "Spice recommendation API", subapis: [] },
                { _id: "cocktaildb", name: "CocktailDB", description: "Cocktail database API", subapis: [] }
            ];

            // Create a default enriched API structure even if we don't have metrics data
            const enriched = baseApis.map((api) => {
                return {
                    ...api,
                    usage: 0,
                    subapis: api.subapis || []
                };
            });

            // If we have metrics data, enhance the enriched APIs
            if (metricsStatus) {
                // Try to extract target usage data if available
                const targetUsageMap = {};
                if (metricsStatus?.proxy?.allTargets && Array.isArray(metricsStatus.proxy.allTargets)) {
                    metricsStatus.proxy.allTargets.forEach(({ target, count }) => {
                        if (target && count !== undefined) {
                            targetUsageMap[target] = count;
                        }
                    });
                }

                // Try to extract endpoint data if available
                let endpointList = [];
                if (metricsStatus.allEndpoints && Array.isArray(metricsStatus.allEndpoints)) {
                    endpointList = metricsStatus.allEndpoints;
                } else if (metricsStatus.proxy?.allProxiedEndpoints && Array.isArray(metricsStatus.proxy.allProxiedEndpoints)) {
                    endpointList = metricsStatus.proxy.allProxiedEndpoints;
                }

                if (Array.isArray(endpointList) && endpointList.length > 0) {
                    // Map the endpoints to their corresponding API
                    const groupedSubApis = {};

                    endpointList.forEach((endpoint) => {
    if (!endpoint) return;
    const endpointStr = typeof endpoint === 'string' ? endpoint : endpoint.endpoint || "";
    const count = typeof endpoint === 'object' ? (endpoint.count || 0) : 0;
    const avgResponseTime = typeof endpoint === 'object' ? (endpoint.avgResponseTime || 0) : 0;

    if (!endpointStr) return;

    // Extract just the path part (remove "GET " prefix)
    const path = endpointStr.includes(' ') ? endpointStr.split(' ')[1] : endpointStr;

    // Hard-coded for RecipeDB & FlavorDB (their proxy URLs are ambiguous)
    let apiName = null;
    if (path.includes("/recipe") || path.includes("/recipe2-api")) {
        apiName = "RecipeDB";
    } else if (path.includes("/api/") || path.includes("/flavor") || path.includes("/flavordb")) {
        apiName = "FlavorDB";
    } else {
        // Dynamic matching for all other APIs (SpiceRx, CocktailDB, future APIs)
        enriched.forEach((api) => {
            if (!api.url || ["api", "recipe", "recipe2-api"].includes(api.url)) return; // skip ambiguous ones
            if (path.startsWith(`/${api.url}/`) || path === `/${api.url}`) {
                apiName = api.name;
            }
        });
    }

    if (!apiName) return;

    if (!groupedSubApis[apiName]) groupedSubApis[apiName] = [];
    groupedSubApis[apiName].push({ endpointStr, count, avgResponseTime });
});

                    
                    // Update the enriched APIs with this data
                    
enriched.forEach((api, index) => {
    // Hard-coded target keys for RecipeDB & FlavorDB
    // because their MongoDB url ≠ proxy key
    const targetKey =
        api.name === "RecipeDB" ? "recipe2-api" :
        api.name === "FlavorDB" ? "api" :
        api.url; // SpiceRx, CocktailDB use their url directly

    if (targetUsageMap[targetKey] !== undefined) {
        enriched[index].usage = targetUsageMap[targetKey];
    }

    // Merge metrics into MongoDB subapis
    if (api.subapis && api.subapis.length > 0) {
        enriched[index].subapis = api.subapis.map((subapi) => {
            const match = (groupedSubApis[api.name] || []).find(ep =>
                ep.endpointStr.includes(subapi.url)
            );
            return {
                ...subapi,
  
              usage: match?.count || 0,
                avgResponseTime: match?.avgResponseTime || null
            };
        });
    }
});
                }
            }

            setEnrichedApis(enriched);

            // Calculate max usage across all APIs for the main catalog view
            if (enriched.length > 0) {
                const allUsages = enriched.map(api => api.usage || 0);
                const maxUsage = Math.max(...allUsages);
                setMaxApiUsage(maxUsage > 0 ? maxUsage : 10000);
            }
        } else {
            // Process actual API data
            const enriched = apis.map((api) => {
                return {
                    ...api,
                    usage: 0,
                    subapis: api.subapis || []
                };
            });

            // If we have metrics data, enhance the enriched APIs
            if (metricsStatus) {
                // Try to extract target usage data if available
                const targetUsageMap = {};
                if (metricsStatus?.proxy?.allTargets && Array.isArray(metricsStatus.proxy.allTargets)) {
                    metricsStatus.proxy.allTargets.forEach(({ target, count }) => {
                        if (target && count !== undefined) {
                            targetUsageMap[target] = count;
                        }
                    });
                }

                // Try to extract endpoint data if available
                let endpointList = [];
                if (metricsStatus.allEndpoints && Array.isArray(metricsStatus.allEndpoints)) {
                    endpointList = metricsStatus.allEndpoints;
                } else if (metricsStatus.proxy?.allProxiedEndpoints && Array.isArray(metricsStatus.proxy.allProxiedEndpoints)) {
                    endpointList = metricsStatus.proxy.allProxiedEndpoints;
                }

                if (Array.isArray(endpointList) && endpointList.length > 0) {
                    // Map the endpoints to their corresponding API
                    const groupedSubApis = {};

                    endpointList.forEach((endpoint) => {
    if (!endpoint) return;
    const endpointStr = typeof endpoint === 'string' ? endpoint : endpoint.endpoint || "";
    const count = typeof endpoint === 'object' ? (endpoint.count || 0) : 0;
    const avgResponseTime = typeof endpoint === 'object' ? (endpoint.avgResponseTime || 0) : 0;

    if (!endpointStr) return;

    // Extract just the path part (remove "GET " prefix)
    const path = endpointStr.includes(' ') ? endpointStr.split(' ')[1] : endpointStr;

    // Hard-coded for RecipeDB & FlavorDB (their proxy URLs are ambiguous)
    let apiName = null;
    if (path.includes("/recipe") || path.includes("/recipe2-api")) {
        apiName = "RecipeDB";
    } else if (path.includes("/api/") || path.includes("/flavor") || path.includes("/flavordb")) {
        apiName = "FlavorDB";
    } else {
        // Dynamic matching for all other APIs (SpiceRx, CocktailDB, future APIs)
        enriched.forEach((api) => {
            if (!api.url || ["api", "recipe", "recipe2-api"].includes(api.url)) return; // skip ambiguous ones
            if (path.startsWith(`/${api.url}/`) || path === `/${api.url}`) {
                apiName = api.name;
            }
        });
    }

    if (!apiName) return;

    if (!groupedSubApis[apiName]) groupedSubApis[apiName] = [];
    groupedSubApis[apiName].push({ endpointStr, count, avgResponseTime });
});

                    
                    // Update the enriched APIs with this data
                    
enriched.forEach((api, index) => {
    // Hard-coded target keys for RecipeDB & FlavorDB
    // because their MongoDB url ≠ proxy key
    const targetKey =
        api.name === "RecipeDB" ? "recipe2-api" :
        api.name === "FlavorDB" ? "api" :
        api.url; // SpiceRx, CocktailDB use their url directly

    if (targetUsageMap[targetKey] !== undefined) {
        enriched[index].usage = targetUsageMap[targetKey];
    }

    // Merge metrics into MongoDB subapis
    if (api.subapis && api.subapis.length > 0) {
        enriched[index].subapis = api.subapis.map((subapi) => {
            const match = (groupedSubApis[api.name] || []).find(ep =>
                ep.endpointStr.includes(subapi.url)
            );
            return {
                ...subapi,
  
              usage: match?.count || 0,
                avgResponseTime: match?.avgResponseTime || null
            };
        });
    }
});
                }
            }

            setEnrichedApis(enriched);

            // Calculate max usage across all APIs for the main catalog view
            if (enriched.length > 0) {
                const allUsages = enriched.map(api => api.usage || 0);
                const maxUsage = Math.max(...allUsages);
                setMaxApiUsage(maxUsage > 0 ? maxUsage : 10000);
            }
        }
    }, [apis, metricsStatus]); // Only run when apis or metricsStatus change

    const apiLogos = {
        RecipeDB: recipeDB_logo,
        FlavorDB: flavorDB_logo,
        SustainableFoodDB: sustainableDB_logo,
        dietRx: dietRx_logo,
        SpiceRx: spiceRx_logo,
        spicerx: spiceRx_logo,
        SpiceRX: spiceRx_logo,
        CocktailDB: cocktailDB_logo,
        CocktailDb: cocktailDB_logo,
        cocktaildb: cocktailDB_logo
    };

    // Unified lime-green accent color for all API cards (matching the design)
    const accentColor = "#8CB73D";
    const accentColorLight = "#9ACD32";

    const handleApiClick = (api) => setSelectedApi(api);
    const handleBack = () => {
        setSelectedApi(null);
        setSearchTerm("");
        setSortField("usage");
        setSortOrder("desc");
        setUsageFilter("all");
    };

    const getSortValue = (item, field) => {
        if (field === "name") return item.name?.toLowerCase() || "";
        if (field === "usage") return Number(item.usage || 0);
        if (field === "avgResponseTime") {
            const responseTime = item.avgResponseTime;
            if (!responseTime) return 0;

            if (typeof responseTime === 'string') {
                const numericValue = parseFloat(responseTime);
                return isNaN(numericValue) ? 0 : numericValue;
            }
            return Number(responseTime);
        }
        return item[field];
    };

    const filterAndSort = (items) => {
        return items
            .filter((item) => {
                const matchesSearch =
                    !searchTerm ||
                    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    item.description?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
                    item.url?.toLowerCase()?.includes(searchTerm.toLowerCase());
                const usage = item.usage || 0;
                const matchesUsage =
                    usageFilter === "all" ||
                    (usageFilter === "high" && usage >= 1000) ||
                    (usageFilter === "medium" && usage < 1000 && usage >= 100) ||
                    (usageFilter === "low" && usage < 100);
                return matchesSearch && matchesUsage;
            })
            .sort((a, b) => {
                let valA = getSortValue(a, sortField);
                let valB = getSortValue(b, sortField);

                if (valA === undefined || valA === null) valA = "";
                if (valB === undefined || valB === null) valB = "";

                if (sortField === "usage" || sortField === "avgResponseTime") {
                    return sortOrder === "asc" ? valA - valB : valB - valA;
                }
                if (typeof valA === "string" && typeof valB === "string") {
                    return sortOrder === "asc"
                        ? valA.localeCompare(valB)
                        : valB.localeCompare(valA);
                }
                return 0;
            });
    };

    const formatNumber = (num) => {
        if (num === undefined || num === null) return '0';
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return num.toString();
    };

    const calculateMaxUsage = (subapis) => {
        if (!subapis || subapis.length === 0) return 1000;
        const allUsages = subapis.map(api => api.usage || 0);
        const maxUsage = Math.max(...allUsages);
        return maxUsage > 0 ? maxUsage : 1000;
    };

    const getUsagePercentage = (usage, maxUsage) => {
        return Math.max(Math.min(usage / maxUsage * 100, 100), 5);
    };

    const renderUsageBar = (usage, maxUsage) => {
        const percentage = getUsagePercentage(usage, maxUsage);
        return (
            <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#e5e7eb' }}>
                <div
                    className="h-full rounded-full"
                    style={{
                        width: `${percentage}%`,
                        backgroundColor: accentColor
                    }}
                />
            </div>
        );
    };

    const renderSubApisTable = () => {
        if (!selectedApi || !selectedApi.subapis) return null;

        const filteredSubApis = filterAndSort(selectedApi.subapis);
        const maxUsage = calculateMaxUsage(selectedApi.subapis);

        return (
            <MotionDiv
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="mt-6"
            >
                {/* Header section */}
                <MotionDiv variants={itemVariants} className="flex flex-wrap items-center justify-between gap-4 mb-5">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleBack}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#2A2B2D] hover:bg-[#323335] text-gray-300 text-sm font-medium transition-colors"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
                            <span>Back</span>
                        </button>
                        <h1 className="text-xl font-bold text-white">{selectedApi.name} APIs</h1>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => {
                                setSearchTerm("");
                                setSortField("usage");
                                setSortOrder("desc");
                                setUsageFilter("all");
                            }}
                            className="p-2 rounded-lg bg-[#2A2B2D] hover:bg-[#323335] text-gray-300 transition-all cursor-pointer"
                            title="Refresh endpoint list"
                        >
                            <HiOutlineRefresh className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`p-2 rounded-lg transition-all ${showFilters ? 'bg-[#8CB73D]/20 text-[#8CB73D]' : 'bg-[#2A2B2D] hover:bg-[#323335] text-gray-300'}`}
                            title="Show filters"
                        >
                            <HiOutlineFilter className="w-5 h-5" />
                        </button>
                    </div>
                </MotionDiv>

                {/* Search */}
                <MotionDiv variants={itemVariants} className="mb-5">
                    <div className="relative w-full sm:w-96">
                        <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search endpoints..."
                            className="w-full pl-11 pr-10 py-2.5 rounded-xl border border-gray-700/50 bg-[#2A2B2D] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="absolute inset-y-0 right-0 flex items-center pr-3"
                                title="Clear search"
                            >
                                <HiX className="h-4 w-4 text-gray-400 hover:text-gray-300" />
                            </button>
                        )}
                    </div>
                </MotionDiv>

                {/* Filters */}
                {showFilters && (
                    <MotionDiv
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="bg-[#2A2B2D] rounded-xl border border-gray-700/50 shadow-sm p-5 grid grid-cols-1 md:grid-cols-2 gap-5 mb-5"
                    >
                        <div>
                            <label className="block text-sm font-bold text-[#8C9086] uppercase tracking-wider mb-2">Sort By</label>
                            <select
                                value={sortField}
                                onChange={(e) => setSortField(e.target.value)}
                                className="w-full rounded-xl border border-gray-700/50 bg-[#1A1A1A] text-white py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50 cursor-pointer text-sm font-medium"
                            >
                                <option value="usage">Usage</option>
                                <option value="name">Name</option>
                                <option value="avgResponseTime">Avg Response Time</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-[#8C9086] uppercase tracking-wider mb-2">Order</label>
                            <select
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                className="w-full rounded-xl border border-gray-700/50 bg-[#1A1A1A] text-white py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50 cursor-pointer text-sm font-medium"
                            >
                                <option value="asc">Ascending</option>
                                <option value="desc">Descending</option>
                            </select>
                        </div>
                    </MotionDiv>
                )}

                {/* Endpoints Table */}
                <MotionDiv variants={itemVariants} className="w-full bg-[#E8ECDA] rounded-3xl overflow-hidden relative border border-white/5 shadow-xl mt-2">
                    <div className="overflow-x-auto custom-scrollbar">
                        <table className="min-w-full text-left border-collapse">
                            <thead className="bg-[#E8ECDA] border-b border-[#D5DABF]">
                                <tr>
                                    <th className="px-6 py-4 text-xs font-bold text-[#8C9086] uppercase tracking-wider">Endpoint</th>
                                    <th className="px-6 py-4 text-xs font-bold text-[#8C9086] uppercase tracking-wider">URL</th>
                                    <th className="px-6 py-4 text-xs font-bold text-[#8C9086] uppercase tracking-wider">Usage</th>
                                    <th className="px-6 py-4 text-xs font-bold text-[#8C9086] uppercase tracking-wider">Avg Response Time</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-black/[0.04]">
                                {filteredSubApis.length > 0 ? (
                                    filteredSubApis.map((subapi) => (
                                        <tr
                                            key={subapi._id}
                                            className="hover:bg-[#DEE3D0] transition-colors group"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="w-7 h-7 rounded-md flex items-center justify-center bg-black/5 text-[#5C6353]">
                                                        <FontAwesomeIcon icon={faCode} className="text-xs" />
                                                    </div>
                                                    <div className="ml-3">
                                                        <div className="text-sm font-semibold text-[#161616]">
                                                            {subapi.name}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm">
                                                <code className="font-mono bg-black/5 px-2 py-1 rounded text-[#3A3A3A] text-xs font-bold border border-black/10">
                                                    {subapi.url}
                                                </code>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="w-40">
                                                    <div className="flex items-center justify-between mb-1.5">
                                                        <span className="text-sm font-bold text-[#161616]">
                                                            {formatNumber(subapi.usage)} calls
                                                        </span>
                                                    </div>
                                                    <div className="w-full h-1.5 rounded-full overflow-hidden bg-[#D5DABF]">
                                                        <div
                                                            className="h-full rounded-full transition-all duration-300"
                                                            style={{
                                                                width: `${getUsagePercentage(subapi.usage, maxUsage)}%`,
                                                                backgroundColor: accentColor
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[#3A3A3A] font-bold">
                                                {subapi.avgResponseTime ?
                                                    (typeof subapi.avgResponseTime === 'string' ?
                                                        subapi.avgResponseTime :
                                                        `${subapi.avgResponseTime.toFixed(0)} ms`) :
                                                    '-'}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-20 text-center">
                                            <div className="flex flex-col items-center justify-center space-y-4">
                                                <div className="w-16 h-16 bg-[#D5DABF]/30 rounded-full flex items-center justify-center text-[#8C9086]">
                                                    <HiOutlineExclamation className="w-8 h-8" />
                                                </div>
                                                <p className="text-[#8C9086] font-medium text-sm">No endpoints found</p>
                                                <p className="text-[#8C9086] text-xs mt-1">Try adjusting your search or filter criteria</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </MotionDiv>
            </MotionDiv>
        );
    };

    const filteredApis = filterAndSort(
        (enrichedApis || [])
            .filter(api => api && api.name)
            .sort((a, b) => {
                const order = ["RecipeDB", "FlavorDB", "SustainableFoodDB", "dietRx", "SpiceRx", "CocktailDB"];
                const indexA = order.indexOf(a.name);
                const indexB = order.indexOf(b.name);
                return (indexA !== -1 ? indexA : 999) - (indexB !== -1 ? indexB : 999);
            })
    );

    return (
        <MotionDiv
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="w-full"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
            <MotionDiv variants={itemVariants} className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        API Catalog
                    </h2>
                </div>
            </MotionDiv>

            {!selectedApi ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                    {filteredApis.map((api) => {
                        return (
                            <MotionDiv
                                key={api._id}
                                variants={itemVariants}
                                className="group relative overflow-hidden rounded-3xl shadow-md transition-shadow duration-300 ease-out hover:shadow-xl flex flex-col border border-white/5"
                                onClick={() => handleApiClick(api)}
                                style={{
                                    cursor: "pointer",
                                    backgroundColor: "#E8ECDA",
                                    transform: "translateY(0)",
                                    transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                                    willChange: "transform, box-shadow",
                                    backfaceVisibility: "hidden"
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
                                onMouseDown={(e) => { e.currentTarget.style.transform = "translateY(-2px) scale(0.98)"; }}
                                onMouseUp={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; }}
                            >
                                <div className="px-6 pt-6 pb-5">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="w-16 h-16 flex items-center justify-center rounded-2xl overflow-hidden p-2 shadow-sm bg-white/60 backdrop-blur-sm border border-black/5">
                                            <img
                                                src={apiLogos[api.name] || apiLogos[String(api.name || "").toLowerCase()]}
                                                alt={api.name}
                                                className="max-h-12 max-w-12 object-contain"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        </div>

                                        <div className="flex items-center gap-1.5 bg-black/5 px-2.5 py-1 rounded-full border border-black/10">
                                            <div className="h-2 w-2 rounded-full" style={{ backgroundColor: accentColor }}></div>
                                            <span className="text-xs font-bold text-[#3A3A3A]">ACTIVE</span>
                                        </div>
                                    </div>

                                    <h3 className="text-xl font-bold text-[#161616] mb-1">{api.name}</h3>
                                    <p className="text-sm font-medium text-[#8C9086] mb-4">
                                        Enterprise Data Service
                                    </p>

                                    <div className="mb-1">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-bold text-[#8C9086] uppercase tracking-wider">API usage</span>
                                            <span className="text-sm font-bold text-[#161616]">{formatNumber(api.usage)}</span>
                                        </div>
                                        <div className="w-full h-1.5 rounded-full overflow-hidden bg-[#D5DABF]">
                                            <div
                                                className="h-full rounded-full transition-all duration-300"
                                                style={{
                                                    width: `${getUsagePercentage(api.usage, maxApiUsage)}%`,
                                                    backgroundColor: accentColor
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="px-6 py-4 flex items-center justify-between border-t border-[#D5DABF] bg-black/[0.03]">
                                    <div className="flex items-center gap-2">
                                        <FontAwesomeIcon icon={faShieldAlt} className="text-xs text-[#8C9086]" />
                                        <span className="text-xs font-semibold text-[#8C9086]">Auth Required</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 group-hover:translate-x-1 transition-transform">
                                        <span className="text-xs font-bold text-[#161616] group-hover:text-blue-600 transition-colors">Explore</span>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" className="text-[#161616] group-hover:text-blue-600 transition-colors" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>
                            </MotionDiv>
                        );
                    })}
                </div>
            ) : (
                renderSubApisTable()
            )}
        </MotionDiv>
    );
};

export default ApiCatalog;